// Form.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { queueMail } from './SendMail';
import './ApplyForm.css'; // Import the CSS file
import StatusMessage from './components/StatusMessage';
import { useStatusMessage } from './hooks/useStatusMessage';

function Form() {
  const [formData, setFormData] = useState({
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    childName: '',
    childGrade: '',
    subject: '',
    preferredTime: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const statusMessage = useStatusMessage();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
      // Basic validation
    if (!formData.parentName || !formData.parentEmail || !formData.parentPhone) {
      statusMessage.showError('Please provide your name, email, and phone number so we can contact you.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      
      // Send email notification to BrightBridge team
      const emailContent = `
        <h2>New Consultation Request - BrightBridge</h2>
        <p><strong>Contact Information:</strong></p>
        <ul>
          <li>Name: ${formData.parentName}</li>
          <li>Email: ${formData.parentEmail}</li>
          <li>Phone: ${formData.parentPhone}</li>
        </ul>
        
        <p><strong>Student Details:</strong></p>
        <ul>
          <li>Child's Name: ${formData.childName || 'Not provided'}</li>
          <li>Grade Level: ${formData.childGrade || 'Not specified'}</li>
          <li>Subject Interest: ${formData.subject || 'Not specified'}</li>
        </ul>
        
        <p><strong>Meeting Preferences:</strong></p>
        <ul>
          <li>Preferred Contact Time: ${formData.preferredTime || 'Not specified'}</li>
          <li>Additional Information: ${formData.additionalInfo || 'None provided'}</li>
        </ul>
        
        <p><em>Please follow up within 24 hours to schedule their consultation.</em></p>
      `;      await queueMail({
        to: ['brightbridgetutors@gmail.com'], // Send to the correct business email
        subject: `New Consultation Request - ${formData.parentName}`,
        text: `New consultation request from ${formData.parentName} (${formData.parentEmail}). Phone: ${formData.parentPhone}`,
        html: emailContent
      });
      
// Send confirmation email to parent
      const confirmationEmail = `
        <h2>Thank you for contacting BrightBridge</h2>
        <p>Dear ${formData.parentName},</p>
        
        <p>Thank you for your interest in BrightBridge's academic support services. We've received your consultation request and one of our academic advisors will contact you within 24 hours.</p>
        
        <p><strong>Next steps:</strong></p>
        <ul>
          <li>We'll call you at ${formData.parentPhone} to discuss your needs</li>
          <li>We'll schedule a convenient time for your complimentary consultation</li>
          <li>We'll create a personalized learning plan for ${formData.childName || 'your child'}</li>
          <li>We'll connect you with one of our experienced tutors</li>
        </ul>
        
        <p>We look forward to supporting your child's academic success.</p>
          <p>Best regards,<br>
        The BrightBridge Team<br>
        brightbridgetutors@gmail.com</p>
      `;      await queueMail({
        to: [formData.parentEmail],
        subject: 'BrightBridge Consultation Request Received',
        text: `Dear ${formData.parentName}, thank you for contacting BrightBridge! We'll call you within 24 hours.`,
        html: confirmationEmail
      });

      setSubmitted(true);
      
    } catch (error) {
      console.error('Error sending email:', error);
      statusMessage.showError('There was an issue submitting your request. Please try again or call us directly.');
    }
    
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="container">        <div id="contact" className="success-message">
          <h3>Thank You for Your Interest</h3>
          <div className="success-content">
            <p>Hello <strong>{formData.parentName}</strong>,</p>
            <p>We've received your consultation request and will contact you at <strong>{formData.parentPhone}</strong> within 24 hours to schedule your meeting.</p>
            
            <div className="next-steps">
              <h4>What to expect:</h4>
              <ul>
                <li>• Confirmation email sent to {formData.parentEmail}</li>
                <li>• Personal call from our academic advisor</li>
                <li>• Complimentary consultation scheduling</li>
                <li>• Customized learning plan discussion</li>
              </ul>
            </div>
            
            <button 
              className="cta-button"
              onClick={() => navigate('/')}
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }  return (
    <div className="container">
      <div className="hero-section">
        <h1>Schedule Your Consultation</h1>
        <p className="hero-subtitle">Connect with our experienced academic advisors to discuss your child's educational goals and create a personalized learning plan.</p>
      </div>
        <form id="contact" onSubmit={handleSubmit}>
        <h3>Request a Meeting</h3>
        <p className="form-subtitle">Please provide your contact information and we'll reach out to schedule your consultation.</p>
          <fieldset>
          <input
            type="text"
            name="parentName"
            placeholder="Full Name"
            value={formData.parentName}
            onChange={handleChange}
            required
            autoFocus
          />
        </fieldset>
        <fieldset>
          <input
            type="email"
            name="parentEmail"
            placeholder="Email Address"
            value={formData.parentEmail}
            onChange={handleChange}
            required
          />
        </fieldset>
        <fieldset>
          <input
            type="tel"
            name="parentPhone"
            placeholder="Phone Number"
            value={formData.parentPhone}
            onChange={handleChange}
            required
          />
        </fieldset>

        <fieldset>
          <input
            type="text"
            name="childName"
            placeholder="Child's Name (Optional)"
            value={formData.childName}
            onChange={handleChange}
          />
        </fieldset>
        <fieldset>
          <select
            name="childGrade"
            value={formData.childGrade}
            onChange={handleChange}
          >
            <option value="">Grade Level (Optional)</option>
            <option value="Elementary (K-5)">Elementary (K-5)</option>
            <option value="Middle School (6-8)">Middle School (6-8)</option>
            <option value="High School (9-12)">High School (9-12)</option>
            <option value="University/College">University/College</option>
          </select>
        </fieldset>
        <fieldset>
          <select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
          >
            <option value="">Subject of Interest (Optional)</option>
            <option value="Mathematics">Mathematics</option>
            <option value="English & Language Arts">English & Language Arts</option>
            <option value="Sciences">Sciences</option>
            <option value="Social Studies">Social Studies</option>
            <option value="World Languages">World Languages</option>
            <option value="Test Preparation">Test Preparation</option>
            <option value="Study Skills">Study Skills</option>
            <option value="Multiple Subjects">Multiple Subjects</option>
            <option value="Other">Other</option>
          </select>
        </fieldset>

        <fieldset>
          <select
            name="preferredTime"
            value={formData.preferredTime}
            onChange={handleChange}
          >
            <option value="">Preferred Contact Time</option>
            <option value="Morning (9AM-12PM)">Morning (9AM-12PM)</option>
            <option value="Afternoon (12PM-5PM)">Afternoon (12PM-5PM)</option>
            <option value="Evening (5PM-8PM)">Evening (5PM-8PM)</option>
            <option value="Weekend">Weekend</option>
            <option value="Flexible">Flexible</option>
          </select>
        </fieldset>
        <fieldset>
          <textarea
            name="additionalInfo"
            placeholder="Additional information or specific goals you'd like to discuss (Optional)"
            value={formData.additionalInfo}
            onChange={handleChange}
            rows="3"
          ></textarea>
        </fieldset>        <fieldset>
          <button
            type="submit"
            id="contact-submit"
            disabled={isSubmitting}
            className="cta-button"
          >
            {isSubmitting ? 'Submitting Request...' : 'Schedule Consultation'}
          </button>
        </fieldset>
          <p className="guarantee-text">
          We'll contact you within 24 hours to schedule your consultation at a time that works for you.
        </p>
      </form>

      <StatusMessage
        message={statusMessage.message}
        type={statusMessage.type}
        isVisible={statusMessage.isVisible}
        onClose={statusMessage.hideMessage}
        position="top-center"
      />
    </div>
  );
}

export default Form;
